import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, first, skip } from 'rxjs/operators';
import { MessagingService } from './messaging.service';
import { isMessagingDocumentType } from './messaging.models';

/**
 * The postMessage types of the messaging bridge. They are added to the protocol the hosts
 * already speak with micro-UIs (REQUEST_TOKEN, NAVIGATE, ROUTE_CHANGE…):
 *
 *   from the frame   REQUEST_MESSAGING_STATUS                             answered with MESSAGING_STATUS { enabled }
 *   from the shell   MESSAGING_STATUS { enabled }                         sent again whenever messaging is switched on or off
 *   from the frame   OPEN_DOCUMENT_THREAD { documentType, documentUid }   the panel opens on the thread, then
 *   from the shell   DOCUMENT_THREAD_RESULT { documentType, documentUid, ok, status?, message?, shown? }
 *
 * `shown` means the shell has already told the user about the failure, so the frame should not.
 */
export const MESSAGING_BRIDGE = {
  requestStatus: 'REQUEST_MESSAGING_STATUS',
  status: 'MESSAGING_STATUS',
  openThread: 'OPEN_DOCUMENT_THREAD',
  threadResult: 'DOCUMENT_THREAD_RESULT',
} as const;

/** The framed micro-UI's window: the MessageEvent's `source`, or the iframe's contentWindow. */
export type FrameWindow = Pick<Window, 'postMessage'>;

/**
 * Zeymo Messages for a micro-UI framed by the shell (ZEY-2907). A record page in a micro-UI
 * (ca-printplates-ui's plate) offers Discuss, and the thread opens in the shell's messages panel.
 * That is the same panel and the same thread as the Discuss button on the Angular screens
 * (DiscussButtonComponent), and the same thread mobile opens.
 *
 * The host checks that a message came from its frame's origin before passing it here. Replies
 * go back to that window and origin only.
 */
@Injectable({ providedIn: 'root' })
export class IframeMessagingBridgeService {
  constructor(private messaging: MessagingService) {}

  /** Answers one request from a frame. Returns false for a message that is not the bridge's. */
  handle(data: any, source: FrameWindow | null | undefined, origin: string): boolean {
    switch (data?.type) {
      case MESSAGING_BRIDGE.requestStatus:
        this.messaging.enabled$.pipe(first()).subscribe((enabled) => this.postStatus(source, origin, enabled));
        return true;
      case MESSAGING_BRIDGE.openThread:
        this.openThread(data, source, origin);
        return true;
      default:
        return false;
    }
  }

  /**
   * Sends MESSAGING_STATUS to the frame again whenever the tenant's messaging is switched on or
   * off. The frame asks for the current value itself, so only changes are pushed.
   */
  watch(frame: () => FrameWindow | null | undefined, origin: string): Subscription {
    return this.messaging.enabled$
      .pipe(distinctUntilChanged(), skip(1))
      .subscribe((enabled) => this.postStatus(frame(), origin, enabled));
  }

  private postStatus(target: FrameWindow | null | undefined, origin: string, enabled: boolean): void {
    target?.postMessage({ type: MESSAGING_BRIDGE.status, enabled }, origin);
  }

  private openThread(data: any, source: FrameWindow | null | undefined, origin: string): void {
    const documentType = data?.documentType;
    const documentUid = data?.documentUid;
    const reply = (result: { ok: boolean; status?: number; message?: string; shown?: boolean }) =>
      source?.postMessage({ type: MESSAGING_BRIDGE.threadResult, documentType, documentUid, ...result }, origin);

    if (!isMessagingDocumentType(documentType) || typeof documentUid !== 'string' || !documentUid.trim()) {
      reply({ ok: false, status: 400 });
      return;
    }

    this.messaging.openDocument(documentType, documentUid).subscribe({
      next: () => reply({ ok: true }),
      error: (err) => {
        console.error('[Messaging] Could not open the thread a module asked for', err);
        const status = typeof err?.status === 'number' ? err.status : 0;
        reply({
          ok: false,
          status,
          message: validationText(err),
          // A refused write already shows the shell's "not authorized" dialog
          // (HttpErrorHandlerService.publishError -> AppComponent), so the frame says nothing more.
          shown: status === 403,
        });
      },
    });
  }
}

/** The API's own words for a 400, as the messages panel shows them. */
function validationText(err: any): string | undefined {
  const errors = err?.error?.errors;
  if (errors && typeof errors === 'object') {
    const text = Object.values(errors).flat().join(' ');
    if (text) {
      return text;
    }
  }
  return undefined;
}
